// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import Modal from './Modal';
import './CheatsModal.css';
import Button from './Button';
import GameIcon from './GameIcon';
import * as EnhancedEventsConfig from '../config/EnhancedEventsConfig';
import SoundManager from '../utils/system/SoundManager';
import SoundEffects from '../utils/system/SoundEffects';
import MissionManager from '../utils/game/MissionManager';
import ResourceManager from '../utils/game/ResourceManager';
import CrewManager from '../utils/game/CrewManager';
import GameTimeManager from '../utils/game/GameTimeManager';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

// CombatMiniGame is the largest gameplay component (~1,036 LOC).
// Lazy-load it so the cheats modal doesn't drag it into the initial bundle.
const CombatMiniGame = lazy(() => import('./CombatMiniGame/CombatMiniGame'));

/**
 * Developer Cheats Modal
 * 
 * Provides testing interface for triggering random events manually
 * Activated with Alt+E keyboard shortcut
 * 
 * Dynamically discovers all available events from EnhancedEventsConfig
 */
const CheatsModal = ({ isOpen, onClose, onTriggerEvent }) => {
  const [activeTab, setActiveTab] = useState('cheats'); // New: tab state
  const [selectedCategory, setSelectedCategory] = useState('mini games');
  const [showCombatTest, setShowCombatTest] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.SHIP_VOICE) || 'matthew-ship-computer';
  });
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [voiceConfigLoaded, setVoiceConfigLoaded] = useState(false);
  const [selectedSoundPack, setSelectedSoundPack] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.SOUND_EFFECTS_PACK) || 'default';
  });
  const [soundPackOptions, setSoundPackOptions] = useState([]);
  const [missionRefresh, setMissionRefresh] = useState(0); // Trigger re-renders for mission updates
  
  // State for Manager data to avoid calling on every render
  const [crewCount, setCrewCount] = useState(0);
  const [crewMorale, setCrewMorale] = useState(0);
  const [injuredCount, setInjuredCount] = useState(0);
  const [activeMissionsCount, setActiveMissionsCount] = useState(0);
  const [availableMissionsCount, setAvailableMissionsCount] = useState(0);
  const [timeScale, setTimeScale] = useState(1);
  const [gameTime, setGameTime] = useState(0);

  // Load initial Manager data and subscribe to crew updates when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const allCrew = CrewManager.getAllCrew();
    setCrewCount(allCrew.length);
    setCrewMorale(CrewManager.getMorale());
    setInjuredCount(allCrew.filter(c => c.injured).length);
    setActiveMissionsCount(MissionManager.getActiveMissions().length);
    setAvailableMissionsCount(MissionManager.getAvailableMissions().length);
    setTimeScale(GameTimeManager.getTimeScale());
    setGameTime(GameTimeManager.getGameTime());

    const unsubscribe = EventBus.on(EVENT_TYPES.CREW_UPDATED, (data) => {
      setCrewCount(data.crew.length);
      setCrewMorale(data.morale);
      setInjuredCount(data.injuredCrew);
    });
    return unsubscribe;
  }, [isOpen]);

  // Force re-render when mission state changes
  useEffect(() => {
    // This effect runs when missionRefresh changes, ensuring UI updates
    if (missionRefresh > 0) {
    }
  }, [missionRefresh]);

  // Load voice configuration from JSON file
  useEffect(() => {
    const loadVoiceConfig = async () => {
      try {
        const response = await fetch('/sounds/voice/voice-config.json');
        if (response.ok) {
          const config = await response.json();
          setVoiceOptions(config.voices || []);
          setVoiceConfigLoaded(true);
        } else {
          console.warn('[Voice] Failed to load voice-config.json, using fallback');
          setVoiceOptions(getFallbackVoices());
          setVoiceConfigLoaded(true);
        }
      } catch (error) {
        console.error('[Voice] Error loading voice config:', error);
        setVoiceOptions(getFallbackVoices());
        setVoiceConfigLoaded(true);
      }
    };

    if (isOpen) {
      loadVoiceConfig();
    }
  }, [isOpen]);

  // Load sound pack configuration
  useEffect(() => {
    const loadSoundPackConfig = async () => {
      try {
        // Wait a bit for SoundEffects to initialize
        await new Promise(resolve => setTimeout(resolve, 100));
        const packs = SoundEffects.getAvailablePacks();
        setSoundPackOptions(packs);
      } catch (error) {
        console.error('[SoundEffects] Error loading sound pack config:', error);
      }
    };

    if (isOpen) {
      loadSoundPackConfig();
    }
  }, [isOpen]);

  // Fallback voice options if config file fails to load
  const getFallbackVoices = () => [
    { id: 'joanna', name: 'Joanna', description: 'Professional, clear, authoritative', gender: 'female', style: 'normal', path: 'joanna' },
    { id: 'joanna-sultry', name: 'Joanna (Sultry)', description: 'Warm, sophisticated, intimate', gender: 'female', style: 'sultry', path: 'joanna-sultry' },
    { id: 'danielle', name: 'Danielle', description: 'Conversational, friendly', gender: 'female', style: 'normal', path: 'danielle' },
    { id: 'danielle-bimbo', name: 'Danielle (Bimbo)', description: 'Bubbly, breathy, enthusiastic', gender: 'female', style: 'bimbo', path: 'danielle-bimbo' },
    { id: 'ruth-sultry', name: 'Ruth (Sultry)', description: 'Mature, smooth, seductive', gender: 'female', style: 'sultry', path: 'ruth-sultry' },
    { id: 'stephen', name: 'Stephen', description: 'Male, authoritative, professional', gender: 'male', style: 'normal', path: 'stephen' },
    { id: 'matthew-glados', name: 'Matthew (GLaDOS)', description: 'Sarcastic, slow, menacing', gender: 'male', style: 'glados', path: 'matthew-glados' },
  ];

  // Dynamically discover all event categories and events
  const eventCategories = useMemo(() => {
    const categories = {
      'immediate': [],
      'interactive': [],
      'mini games': [],
      'outcomes': []
    };
    
    // Scan all exports from EnhancedEventsConfig
    Object.keys(EnhancedEventsConfig).forEach(exportName => {
      const exportValue = EnhancedEventsConfig[exportName];
      
      // Check if this export contains event objects
      if (typeof exportValue === 'object' && exportValue !== null) {
        const events = Object.values(exportValue).filter(item => 
          item && typeof item === 'object' && item.type && item.title
        );
        
        if (events.length > 0) {
          // Categorize each event based on its characteristics
          events.forEach(event => {
            let categoryName = 'immediate'; // default
            
            // Use explicit category if available
            if (event.category) {
              categoryName = event.category === 'minigame' ? 'mini games' : event.category;
            } else {
              // Fallback to detection logic for events without explicit category
              // Check if event triggers a mini-game
              const hasMinigameAction = event.actions && event.actions.some(action => 
                action.triggersSystem && (
                  action.triggersSystem === 'ASTEROID_MINIGAME' ||
                  action.triggersSystem.includes('MINIGAME') ||
                  action.triggersSystem.includes('GAME')
                )
              );
              
              // Check if event spawns other events (interactive)
              const hasSpawnAction = event.actions && event.actions.some(action => 
                action.spawnsEvent || action.triggersSystem === 'COMBAT_SYSTEM'
              );
              
              // Determine category based on characteristics
              if (hasMinigameAction) {
                categoryName = 'mini games';
              } else if (exportName.toLowerCase().includes('outcome')) {
                categoryName = 'outcomes';
              } else if (hasSpawnAction || exportName.toLowerCase().includes('interactive')) {
                categoryName = 'interactive';
              } else {
                categoryName = 'immediate';
              }
            }
            
            categories[categoryName].push(event);
          });
        }
      }
    });
    
    // Sort events within each category by type name
    Object.keys(categories).forEach(categoryName => {
      categories[categoryName].sort((a, b) => a.type.localeCompare(b.type));
    });
    
    // Remove empty categories
    const nonEmptyCategories = {};
    Object.keys(categories).forEach(categoryName => {
      if (categories[categoryName].length > 0) {
        nonEmptyCategories[categoryName] = categories[categoryName];
      }
    });
    
    return nonEmptyCategories;
  }, []);

  // Get current events for selected category
  const currentEvents = eventCategories[selectedCategory] || [];
  const categoryNames = Object.keys(eventCategories);

  // Auto-select first available category if current selection is invalid
  useEffect(() => {
    if (categoryNames.length > 0 && !categoryNames.includes(selectedCategory)) {
      // Prioritize mini games, then immediate, then others
      const preferredOrder = ['mini games', 'immediate', 'interactive', 'outcomes'];
      const availablePreferred = preferredOrder.find(cat => categoryNames.includes(cat));
      setSelectedCategory(availablePreferred || categoryNames[0]);
    }
  }, [categoryNames, selectedCategory]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.altKey && event.key.toLowerCase() === 'e') {
        event.preventDefault();
        if (isOpen) {
          onClose();
        }
      }
      
      if (isOpen && event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose]);

  const handleTriggerEvent = (eventType) => {
    onTriggerEvent(eventType);
    onClose();
  };

  const handleVoiceChange = (voiceId) => {
    setSelectedVoice(voiceId);
    localStorage.setItem(STORAGE_KEYS.SHIP_VOICE, voiceId);
    SoundManager.clearCache(); // Clear cache when voice changes
  };

  const handleTestVoice = () => {
    // Initialize SoundManager on first interaction
    SoundManager.initialize();
    // Test warp engaged announcement
    SoundManager.play('warp_engaged', { priority: 'high' });
  };

  const handleSoundPackChange = (packId) => {
    setSelectedSoundPack(packId);
    const success = SoundEffects.setSoundPack(packId);
    if (success) {
    }
  };

  const handleTestSoundEffect = () => {
    // Initialize SoundEffects on first interaction
    SoundEffects.initialize();
    // Test laser fire sound
    SoundEffects.play('laser_fire');
  };

  const formatEventName = (eventType) => {
    return eventType.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatCategoryName = (categoryName) => {
    return categoryName.replace(/_/g, ' ').toUpperCase();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="🛠️ DEVELOPER CHEATS"
      size="full"
      closeOnBackdrop={true}
      closeOnEscape={true}
      showCloseButton={true}
      className="cheats-modal-custom"
    >
      <div className="cheats-modal-subtitle">
        Event Testing Interface - {Object.values(eventCategories).flat().length} Total Events
      </div>

      {/* Tab Navigation */}
      <div className="cheats-tabs-nav">
        <button
          className={`cheats-tab-button ${activeTab === 'cheats' ? 'active' : ''}`}
          onClick={() => setActiveTab('cheats')}
        >
          ⚡ CHEATS
        </button>
        <button
          className={`cheats-tab-button ${activeTab === 'audio' ? 'active' : ''}`}
          onClick={() => setActiveTab('audio')}
        >
          🔊 AUDIO
        </button>
        <button
          className={`cheats-tab-button ${activeTab === 'icons' ? 'active' : ''}`}
          onClick={() => setActiveTab('icons')}
        >
          🎨 ICONS
        </button>
        <button
          className={`cheats-tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ SETTINGS
        </button>
      </div>

      <div className="cheats-modal-body">
        {/* Audio Tab */}
        {activeTab === 'audio' && (
          <>
            {/* Voice Selection Section */}
            <div className="cheats-voice-section">
          <h3 className="cheats-section-title">🔊 SHIP COMPUTER VOICE</h3>
          {!voiceConfigLoaded ? (
            <div className="cheats-voice-loading">Loading voice options...</div>
          ) : voiceOptions.length === 0 ? (
            <div className="cheats-voice-error">No voice options available</div>
          ) : (
            <>
              <div className="cheats-voice-grid">
                {voiceOptions.map(voice => (
                  <button
                    key={voice.id}
                    className={`cheats-voice-option ${selectedVoice === voice.id ? 'active' : ''}`}
                    onClick={() => handleVoiceChange(voice.id)}
                    title={voice.description}
                  >
                    <div className="cheats-voice-name">{voice.name}</div>
                    <div className="cheats-voice-description">{voice.description}</div>
                    {selectedVoice === voice.id && (
                      <div className="cheats-voice-active-indicator">✓ ACTIVE</div>
                    )}
                  </button>
                ))}
              </div>
              <div className="cheats-voice-note">
                Voice selection is saved automatically and persists across game sessions.
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={handleTestVoice}
                style={{
                  marginTop: '15px',
                  width: '100%',
                  background: 'linear-gradient(135deg, #00ffff, #0088ff)',
                  border: '2px solid #00ffff'
                }}
              >
                🔊 TEST VOICE - "Warp Drive Engaged"
              </Button>
            </>
          )}
        </div>

        {/* Sound Pack Selection Section */}
        <div className="cheats-voice-section">
          <h3 className="cheats-section-title">🎵 SOUND EFFECTS PACK</h3>
          {soundPackOptions.length === 0 ? (
            <div className="cheats-voice-loading">Loading sound pack options...</div>
          ) : (
            <>
              <div className="cheats-voice-grid">
                {soundPackOptions.map(pack => (
                  <button
                    key={pack.id}
                    className={`cheats-voice-option ${selectedSoundPack === pack.id ? 'active' : ''}`}
                    onClick={() => handleSoundPackChange(pack.id)}
                    title={pack.description}
                  >
                    <div className="cheats-voice-name">{pack.name}</div>
                    <div className="cheats-voice-description">{pack.description}</div>
                    {selectedSoundPack === pack.id && (
                      <div className="cheats-voice-active-indicator">✓ ACTIVE</div>
                    )}
                  </button>
                ))}
              </div>
              <div className="cheats-voice-note">
                Sound pack selection is saved automatically. More packs coming soon!
              </div>
              <Button
                variant="primary"
                size="small"
                onClick={handleTestSoundEffect}
                style={{
                  marginTop: '15px',
                  width: '100%',
                  background: 'linear-gradient(135deg, #ff00ff, #ff0088)',
                  border: '2px solid #ff00ff'
                }}
              >
                🔊 TEST SOUND - "Laser Fire"
              </Button>
            </>
          )}
        </div>
          </>
        )}

        {/* Cheats Tab */}
        {activeTab === 'cheats' && (
          <>
            {/* Resource Testing Section */}
            <div className="cheats-mission-section">
              <h3 className="cheats-section-title">📦 RESOURCE TESTING</h3>
              <div className="cheats-mission-controls">
                <div className="cheats-mission-status">
                  <div className="mission-status-item">
                    <span className="mission-status-label">Food:</span>
                    <span className="mission-status-value">
                      {ResourceManager.getResource('food').toFixed(1)}
                    </span>
                  </div>
                  <div className="mission-status-item">
                    <span className="mission-status-label">Water:</span>
                    <span className="mission-status-value">
                      {ResourceManager.getResource('water').toFixed(1)}
                    </span>
                  </div>
                  <div className="mission-status-item">
                    <span className="mission-status-label">Oxygen:</span>
                    <span className="mission-status-value">
                      {ResourceManager.getResource('oxygen').toFixed(1)}
                    </span>
                  </div>
                  <div className="mission-status-item">
                    <span className="mission-status-label">Medical:</span>
                    <span className="mission-status-value">
                      {ResourceManager.getResource('medical').toFixed(1)}
                    </span>
                  </div>
                  <div className="mission-status-item">
                    <span className="mission-status-label">Parts:</span>
                    <span className="mission-status-value">
                      {ResourceManager.getResource('parts').toFixed(1)}
                    </span>
                  </div>
                </div>
                
                <div className="cheats-mission-buttons">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      ResourceManager.setResource('food', 200);
                      ResourceManager.setResource('water', 400);
                      ResourceManager.setResource('oxygen', 200);
                      ResourceManager.setResource('medical', 100);
                      ResourceManager.setResource('parts', 100);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #00ff88, #00aa44)',
                      border: '2px solid #00ff88'
                    }}
                  >
                    ✅ REFILL ALL RESOURCES
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      ResourceManager.setResource('food', 10);
                      ResourceManager.setResource('water', 20);
                      ResourceManager.setResource('oxygen', 10);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ff4444, #cc0000)',
                      border: '2px solid #ff4444'
                    }}
                  >
                    ⚠️ SET TO CRITICAL
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      // eslint-disable-next-line no-unused-vars
                      const rationing = ResourceManager.toggleRationing();
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ffaa00, #ff8800)',
                      border: '2px solid #ffaa00'
                    }}
                  >
                    🍽️ TOGGLE RATIONING
                  </Button>
                </div>
                
                <div className="cheats-mission-note">
                  Resource testing controls. Refill resources, test critical levels, or toggle rationing mode.
                </div>
              </div>
            </div>

            {/* Crew Testing Section */}
            <div className="cheats-mission-section">
              <h3 className="cheats-section-title">👥 CREW TESTING</h3>
              <div className="cheats-mission-controls">
                <div className="cheats-mission-status">
                  <div className="mission-status-item">
                    <span className="mission-status-label">Crew Count:</span>
                    <span className="mission-status-value">
                      {crewCount}
                    </span>
                  </div>
                  <div className="mission-status-item">
                    <span className="mission-status-label">Morale:</span>
                    <span className="mission-status-value">
                      {crewMorale}%
                    </span>
                  </div>
                  <div className="mission-status-item">
                    <span className="mission-status-label">Injured:</span>
                    <span className="mission-status-value">
                      {injuredCount}
                    </span>
                  </div>
                </div>
                
                <div className="cheats-mission-buttons">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      CrewManager.setMorale(100);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #00ff88, #00aa44)',
                      border: '2px solid #00ff88'
                    }}
                  >
                    😊 MAX MORALE
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      const injured = CrewManager.getAllCrew().filter(c => c.injured).length;
                      if (injured > 0) {
                        CrewManager.healCrew(injured);
                        setMissionRefresh(prev => prev + 1);
                      }
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #00ffff, #0088ff)',
                      border: '2px solid #00ffff'
                    }}
                  >
                    ⚕️ HEAL ALL INJURED
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      CrewManager.injureCrew(3);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ffaa00, #ff8800)',
                      border: '2px solid #ffaa00'
                    }}
                  >
                    🤕 INJURE 3 CREW
                  </Button>
                  
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => {
                      CrewManager.setMorale(15);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ff4444, #cc0000)',
                      border: '2px solid #ff4444'
                    }}
                  >
                    ⚔️ MUTINY RISK
                  </Button>
                </div>
                
                <div className="cheats-mission-note">
                  Crew testing controls. Adjust morale, heal injuries, or test low morale scenarios.
                </div>
              </div>
            </div>

            {/* Mission Testing Section */}
            <div className="cheats-mission-section">
              <h3 className="cheats-section-title">🎯 MISSION TESTING</h3>
              <div className="cheats-mission-controls">
                <div className="cheats-mission-status">
                  <div className="mission-status-item">
                    <span className="mission-status-label">Active Missions:</span>
                    <span className="mission-status-value">
                      {activeMissionsCount}
                    </span>
                  </div>
                  <div className="mission-status-item">
                    <span className="mission-status-label">Available Missions:</span>
                    <span className="mission-status-value">
                      {availableMissionsCount}
                    </span>
                  </div>
                </div>
                
                <div className="cheats-mission-buttons">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      const systems = ['Sol', 'Alpha Centauri', 'Sirius', 'Betelgeuse', 'Vega', 'Arcturus', 'Rigel'];
                      MissionManager.generateMissionBoard('Sol', systems, 5);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #00ffff, #0088ff)',
                      border: '2px solid #00ffff'
                    }}
                  >
                    🎲 GENERATE TEST MISSIONS
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      const active = MissionManager.getActiveMissions();
                      active.forEach(mission => {
                        MissionManager.completeMission(mission.id);
                      });
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #00ff88, #00aa44)',
                      border: '2px solid #00ff88'
                    }}
                  >
                    ✅ COMPLETE ALL MISSIONS
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      const active = MissionManager.getActiveMissions();
                      active.forEach(mission => {
                        mission.timeRemaining += 300; // Add 5 minutes
                      });
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ffaa00, #ff8800)',
                      border: '2px solid #ffaa00'
                    }}
                  >
                    ⏱️ ADD 5 MINUTES
                  </Button>
                  
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => {
                      const active = MissionManager.getActiveMissions();
                      active.forEach(mission => {
                        MissionManager.failMission(mission.id, 'Test failure');
                      });
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ff4444, #cc0000)',
                      border: '2px solid #ff4444'
                    }}
                  >
                    ❌ FAIL ALL MISSIONS
                  </Button>
                </div>
                
                <div className="cheats-mission-note">
                  Mission testing controls. Generate missions, complete them instantly, or add time for testing.
                </div>
              </div>
            </div>

            {/* Event Testing Section */}
            <h3 className="cheats-section-title">⚡ EVENT TESTING</h3>
        
        {categoryNames.length > 1 && (
            <div className="cheats-category-tabs">
              {categoryNames.map(categoryName => (
                <Button 
                  key={categoryName}
                  variant="ghost"
                  size="small"
                  className={`cheats-tab ${selectedCategory === categoryName ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(categoryName)}
                >
                  {formatCategoryName(categoryName)} ({eventCategories[categoryName].length})
                </Button>
              ))}
            </div>
          )}

          <div className="cheats-events-grid">
            {currentEvents.map((event, index) => (
              <Button
                key={`${event.type}-${index}`}
                variant="ghost"
                className="cheats-event-button"
                data-category={selectedCategory}
                onClick={() => handleTriggerEvent(event.type)}
                title={`${event.title}\n\n${event.message}`}
              >
                <div className="cheats-event-icon">
                  {selectedCategory === 'mini games' ? '🎮' : 
                   selectedCategory === 'interactive' ? '🔗' :
                   selectedCategory === 'outcomes' ? '🎯' :
                   event.title.split(' ')[0] || '⚡'}
                </div>
                <div className="cheats-event-name">
                  {formatEventName(event.type)}
                </div>
                <div className="cheats-event-type">
                  {event.type}
                </div>
              </Button>
            ))}
          </div>

          {currentEvents.length === 0 && (
            <div className="cheats-no-events">
              <div className="cheats-no-events-icon">🔍</div>
              <div className="cheats-no-events-text">
                No events found in category "{formatCategoryName(selectedCategory)}"
              </div>
            </div>
          )}

        <div className="cheats-modal-footer">
          <div className="cheats-instructions">
            <strong>Instructions:</strong>
            <ul>
              <li><kbd>Alt + E</kbd> - Toggle cheats modal</li>
              <li><kbd>Esc</kbd> - Close modal</li>
              <li>Click any event button to trigger it immediately</li>
              <li>Events will appear as if they occurred naturally during warp</li>
              <li>Hover over buttons to see full event details</li>
            </ul>
          </div>
          
          <div className="cheats-warning">
            ⚠️ <strong>Development Tool:</strong> This modal is for testing purposes only.
            Events triggered here will affect your game state normally.
          </div>
          
          {/* Test Combat Button */}
          <Button
            variant="primary"
            className="cheat-button"
            onClick={() => setShowCombatTest(true)}
            style={{
              marginTop: '20px',
              width: '100%',
              background: 'linear-gradient(135deg, #ff00ff, #ff0088)',
              border: '2px solid #ff00ff'
            }}
          >
            🎮 TEST NEW COMBAT SYSTEM
          </Button>
        </div>
          </>
        )}

        {/* Icons Tab */}
        {activeTab === 'icons' && (
          <>
            <div className="cheats-icons-section">
              <div className="cheats-section-title">🎨 ICON LIBRARY</div>
              <div className="cheats-section-description">
                Visual icons for aliens, weapons, and status indicators
              </div>

              {/* Alien Icons Preview */}
              <div className="icon-preview-section">
                <h3>Alien Species Icons (29)</h3>
                <div className="icon-preview-grid">
                  {['vogons', 'vulcans', 'borg', 'klingons', 'ferengi', 'babelfish', 'magratheans', 'death'].map(icon => (
                    <div key={icon} className="icon-preview-card">
                      <GameIcon type="alien" name={icon} size={48} glow={true} />
                      <div className="icon-preview-name">{icon}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Weapon Icons Preview */}
              <div className="icon-preview-section">
                <h3>Weapon Icons (5)</h3>
                <div className="icon-preview-grid">
                  {['laser', 'missile', 'torpedo', 'plasma', 'railgun'].map(icon => (
                    <div key={icon} className="icon-preview-card">
                      <GameIcon type="weapon" name={icon} size={48} glow={true} />
                      <div className="icon-preview-name">{icon}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Icons Preview */}
              <div className="icon-preview-section">
                <h3>Status Icons (10)</h3>
                <div className="icon-preview-grid">
                  {['shields', 'hull', 'fuel', 'power', 'weapons', 'engines'].map(icon => (
                    <div key={icon} className="icon-preview-card">
                      <GameIcon type="status" name={icon} size={48} glow={true} />
                      <div className="icon-preview-name">{icon}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="icon-info-box">
                <strong>Usage:</strong> Icons are automatically used in alien encounters and will be integrated throughout the game UI.
                <br /><br />
                <strong>Location:</strong> /public/icons/aliens/, /public/icons/weapons/, /public/icons/status/
              </div>
            </div>
          </>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <>
            <div className="cheats-mission-section">
              <h3 className="cheats-section-title">⏱️ TIME SCALE SETTINGS</h3>
              <div className="cheats-mission-controls">
                <div className="cheats-mission-status">
                  <div className="mission-status-item">
                    <span className="mission-status-label">Current Time Scale:</span>
                    <span className="mission-status-value">
                      {timeScale}x
                    </span>
                  </div>
                  <div className="mission-status-item">
                    <span className="mission-status-label">Game Time Elapsed:</span>
                    <span className="mission-status-value">
                      {(gameTime / 3600).toFixed(1)} hours
                    </span>
                  </div>
                </div>
                
                <div className="cheats-mission-buttons">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      GameTimeManager.setTimeScale(1);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #00ffff, #0088ff)',
                      border: '2px solid #00ffff'
                    }}
                  >
                    🐌 REAL-TIME (1x)
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      GameTimeManager.setTimeScale(60);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #00ff88, #00aa44)',
                      border: '2px solid #00ff88'
                    }}
                  >
                    ⏱️ NORMAL (60x)
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      GameTimeManager.setTimeScale(600);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ffaa00, #ff8800)',
                      border: '2px solid #ffaa00'
                    }}
                  >
                    ⚡ FAST (600x)
                  </Button>
                  
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => {
                      GameTimeManager.setTimeScale(3600);
                      setMissionRefresh(prev => prev + 1);
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #ff00ff, #ff0088)',
                      border: '2px solid #ff00ff'
                    }}
                  >
                    🚀 ULTRA-FAST (3600x)
                  </Button>
                </div>
                
                <div className="cheats-mission-note">
                  Time scale controls resource consumption speed. Normal (60x) = 1 real second = 1 game minute.
                  <br />
                  <strong>Warning:</strong> Higher time scales will consume resources much faster!
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
        {/* Combat Test Modal */}
        {showCombatTest && (
          <Suspense fallback={null}>
            <CombatMiniGame
              enemies={[
                { type: 'SCOUT', name: 'HOSTILE-ALPHA', shields: 50, hull: 50, speed: 1.5 },
                { type: 'INTERCEPTOR', name: 'HOSTILE-BETA', shields: 100, hull: 100, speed: 1.0 },
                { type: 'CRUISER', name: 'HOSTILE-GAMMA', shields: 200, hull: 200, speed: 0.5 }
              ]}
              playerWeapons={[
                { id: 'laser', name: 'Basic Laser', damage: 20 }
              ]}
              onVictory={(stats) => {
                setShowCombatTest(false);
              }}
              onDefeat={(stats) => {
                setShowCombatTest(false);
              }}
              onClose={() => setShowCombatTest(false)}
            />
          </Suspense>
        )}
    </Modal>
  );
};

export default CheatsModal;